import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { MessageSquareText, RotateCcw, Bold } from "lucide-react";
import { LoadingSpinner } from "../LoadingSpinner";
import { ErrorState } from "../ErrorState";
import { getEvent, updateEvent } from "../../services/event.service";
import { getApiErrorMessage } from "../../lib/apiClient";

// Emoji like 📅/🔗 are astral-plane characters, stored as a surrogate pair
// (2 UTF-16 code units) in JS strings. If a cursor/selection index from the
// DOM ever lands between the high and low surrogate of one of these — which
// can happen with arrow-key/selection edge cases in a <textarea> — a plain
// string.slice() at that index splits the pair, producing a lone unpaired
// surrogate in the resulting string. That's invalid UTF-16 and can render or
// serialize unpredictably depending on what touches it next. Nudging the
// index off a low surrogate before using it prevents ever producing that
// split, regardless of what would happen downstream.
function snapToCharBoundary(text: string, index: number): number {
  const isLowSurrogate = (code: number) => code >= 0xdc00 && code <= 0xdfff;
  if (index > 0 && index <= text.length && isLowSurrogate(text.charCodeAt(index - 1))) {
    return index - 1;
  }
  return index;
}

const QUICK_EMOJIS = [
  { emoji: "✅", label: "Checkbox / confirmed" },
  { emoji: "📍", label: "Pin / location" },
  { emoji: "📅", label: "Calendar / date" },
  { emoji: "⏰", label: "Time" },
  { emoji: "🧒", label: "Child" },
  { emoji: "🙏", label: "Please / thank you" },
  { emoji: "🔗", label: "Link" },
];

const DEFAULT_TEMPLATE = [
  "Assalamualaikum {{parentName}},",
  "",
  "Your child/children below have been registered for:",
  "{{eventName}}",
  "Date: {{eventDate}}",
  "Time: {{startTime}} - {{endTime}}",
  "Location: {{location}}",
  "",
  "Children:",
  "{{children}}",
  "",
  "Please confirm attendance using this link:",
  "{{confirmUrl}}",
  "",
  "Thank you.",
].join("\n");

const PLACEHOLDERS = [
  { token: "{{parentName}}", description: "Parent's name" },
  { token: "{{eventName}}", description: "Event name" },
  { token: "{{eventDate}}", description: "Event date" },
  { token: "{{startTime}}", description: "Start time" },
  { token: "{{endTime}}", description: "End time" },
  { token: "{{location}}", description: "Location" },
  { token: "{{children}}", description: "Comma-separated list of the parent's children in this event" },
  { token: "{{confirmUrl}}", description: "The parent's confirmation link" },
];

export function WhatsappTemplatePanel({ eventId }: { eventId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["events", eventId],
    queryFn: () => getEvent(eventId),
  });

  const [template, setTemplate] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (data) {
      setTemplate(data.event.whatsappTemplate ?? DEFAULT_TEMPLATE);
    }
  }, [data]);

  const insertAtCursor = (text: string): void => {
    const el = textareaRef.current;
    if (!el) {
      setTemplate((prev) => prev + text);
      return;
    }
    const start = snapToCharBoundary(template, el.selectionStart ?? template.length);
    const end = snapToCharBoundary(template, el.selectionEnd ?? template.length);
    const next = template.slice(0, start) + text + template.slice(end);
    setTemplate(next);
    // Restore focus + move the cursor to just after the inserted text, after
    // React re-renders the textarea with the new value.
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + text.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  // WhatsApp's own formatting syntax is *text* for bold (not Markdown **text**
  // or HTML) — wraps the current selection, or if nothing is selected, drops
  // in an empty pair with the cursor placed between the asterisks so the
  // Manager can type the bolded part next.
  const toggleBoldSelection = (): void => {
    const el = textareaRef.current;
    if (!el) return;
    const start = snapToCharBoundary(template, el.selectionStart ?? template.length);
    const end = snapToCharBoundary(template, el.selectionEnd ?? template.length);
    const selected = template.slice(start, end);

    // Toggle off if the selection is already wrapped in exactly one pair of
    // asterisks, so clicking Bold again on already-bolded text un-bolds it.
    const alreadyBold = selected.length >= 2 && selected.startsWith("*") && selected.endsWith("*");
    const wrapped = alreadyBold ? selected.slice(1, -1) : `*${selected}*`;

    const next = template.slice(0, start) + wrapped + template.slice(end);
    setTemplate(next);

    requestAnimationFrame(() => {
      el.focus();
      if (selected) {
        // Keep the (now wrapped/unwrapped) text selected.
        el.setSelectionRange(start, start + wrapped.length);
      } else {
        // Nothing was selected — place the cursor between the new asterisks.
        el.setSelectionRange(start + 1, start + 1);
      }
    });
  };

  const mutation = useMutation({
    mutationFn: (value: string | null) => updateEvent(eventId, { whatsappTemplate: value }),
    onSuccess: () => {
      toast.success("WhatsApp template saved");
      queryClient.invalidateQueries({ queryKey: ["events", eventId] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorState message={getApiErrorMessage(error)} />;

  const isCustom = !!data?.event.whatsappTemplate;

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-400">
            <MessageSquareText className="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-100">WhatsApp confirmation message</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Customize the message sent to parents for this event. Event Staff will use whatever message is saved
              here when they click Send WhatsApp — they cannot edit it themselves.
              {!isCustom && " Currently using the default message."}
            </p>
          </div>
        </div>
      </div>

      <div className="card p-4">
        <div className="flex items-center justify-between">
          <label className="label mb-0" htmlFor="whatsapp-template">
            Message template
          </label>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
            onClick={toggleBoldSelection}
            title="Bold selected text (WhatsApp formats *text* as bold)"
            aria-label="Bold selected text"
          >
            <Bold className="h-4 w-4" />
          </button>
        </div>
        <textarea
          ref={textareaRef}
          id="whatsapp-template"
          className="input mt-1 font-mono"
          rows={12}
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
        />
        <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500">
          Select some text and click <Bold className="inline h-3 w-3 -translate-y-px" aria-hidden="true" /> to bold
          it — WhatsApp renders text wrapped in single asterisks (e.g. <code className="rounded bg-slate-100 px-1 dark:bg-slate-700">*{"{{eventName}}"}*</code>) as
          bold in the actual message.
        </p>

        <p className="mt-3 text-xs font-medium text-slate-500 dark:text-slate-400">Insert emoji</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {QUICK_EMOJIS.map((e) => (
            <button
              key={e.emoji}
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-lg transition-colors hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-700"
              onClick={() => insertAtCursor(e.emoji)}
              title={e.label}
              aria-label={`Insert ${e.label}`}
            >
              {e.emoji}
            </button>
          ))}
        </div>

        <p className="mt-3 text-xs font-medium text-slate-500 dark:text-slate-400">Insert placeholder</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {PLACEHOLDERS.map((p) => (
            <button
              key={p.token}
              type="button"
              className="badge bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
              onClick={() => insertAtCursor(p.token)}
              title={p.description}
            >
              {p.token}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={mutation.isPending || !template.trim()}
            onClick={() => mutation.mutate(template)}
          >
            {mutation.isPending ? "Saving..." : "Save Template"}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={mutation.isPending}
            onClick={() => {
              setTemplate(DEFAULT_TEMPLATE);
              mutation.mutate(null);
            }}
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
}
